import type {
    FlowConfigViewModel,
    FlowStepViewModel,
    AgentExecutionViewModel,
    LLMConfigOptionsViewModel,
} from "@models/doctype";
import type { AgentExecutionFormData } from "@utils/builders/flowConfig";
import {
    buildCreateFlowConfigRequest,
    buildFlowConfigFormData,
} from "@utils/builders/flowConfig";

/**
 * Builds a CreateFlowConfigRequest with a single agent updated in the target step.
 */
export const buildAgentUpdateRequest = (
    flowConfig: FlowConfigViewModel,
    step: FlowStepViewModel,
    agent: AgentExecutionViewModel,
    updatedAgent: AgentExecutionFormData,
) => {
    const formData = buildFlowConfigFormData(flowConfig);
    const updatedSteps = formData.steps.map((s) => {
        const isTargetStep = s.stepOrder === step.stepOrder;

        if (s.llmStepConfig) {
            const executions = s.llmStepConfig.executions?.map((a) => {
                if (isTargetStep && a.domainKey === agent.domainKey) {
                    return updatedAgent;
                }
                return {
                    ...a,
                };
            }) ?? null;

            return {
                stepOrder: s.stepOrder,
                stepType: s.stepType,
                llmStepConfig: {
                    ...s.llmStepConfig,
                    executions,
                },
            };
        }

        if (s.ocrStepConfig) {
            return {
                stepOrder: s.stepOrder,
                stepType: s.stepType,
                ocrStepConfig: {
                    ...s.ocrStepConfig,
                },
            };
        }

        return { stepOrder: s.stepOrder, stepType: s.stepType };
    });

    return buildCreateFlowConfigRequest({
        ...formData,
        steps: updatedSteps,
    });
};

/**
 * Builds config options from form state, returning null if no options are set.
 */
export const buildConfigOptionsFromForm = (
    opts: {
        temperature: number | string;
        topP: number | string;
        topK: number | string;
        maxOutputTokens: number | string;
        thinkingBudget: number | string;
        seed: number | string;
    },
    existing?: LLMConfigOptionsViewModel | null,
): LLMConfigOptionsViewModel | null => {
    const configOptions: LLMConfigOptionsViewModel = { ...existing };
    delete configOptions.temperature;
    delete configOptions.topP;
    delete configOptions.topK;
    delete configOptions.maxOutputTokens;
    delete configOptions.thinkingBudget;
    delete configOptions.seed;
    if (opts.temperature !== "") configOptions.temperature = Number(opts.temperature);
    if (opts.topP !== "") configOptions.topP = Number(opts.topP);
    if (opts.topK !== "") configOptions.topK = Number(opts.topK);
    if (opts.maxOutputTokens !== "") configOptions.maxOutputTokens = Number(opts.maxOutputTokens);
    if (opts.thinkingBudget !== "") configOptions.thinkingBudget = Number(opts.thinkingBudget);
    if (opts.seed !== "") configOptions.seed = Number(opts.seed);
    return Object.keys(configOptions).length > 0 ? configOptions : null;
};
